'use client'

import { Suspense, useContext, useEffect, useRef } from 'react'
import { Cell, Divider, Navigation, Text } from '@vinted/web-ui'

import { Advertisement } from '@marketplace-web/domain/ads'
import { AdShape } from '@marketplace-web/domain/ads-data'
import { ShowAtBreakpoint, useBreakpoint } from '@marketplace-web/shared/breakpoints'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import { InfiniteScroll } from '@marketplace-web/common-components/infinite-scroll-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state'
import {
  INBOX_URL,
  ConversationListItemType,
} from '@marketplace-web/messaging/conversation-messaging-data'
import { InfoBanner } from '@marketplace-web/info-banner/info-banner-feature'

import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import {
  useConversationContext,
  ConversationListContext,
} from '@marketplace-web/messaging/conversation-providers-feature'

import ConversationListItem from '../ConversationListItem'
import ConversationListEmptyState from '../ConversationListEmptyState'

const ConversationList = () => {
  const { clearConversation } = useConversationContext()
  const translate = useTranslate('conversations.list')
  const infiniteScrollWrapRef = useRef<HTMLDivElement>(null)
  const breakpoints = useBreakpoint()
  const { uiState, conversations, isEndReached, setCurrentPage } =
    useContext(ConversationListContext)
  const { relativeUrl } = useLocation()

  const isInboxPage = relativeUrl === INBOX_URL
  const linkTracking = { target: ClickableElement.MergeMessageListBanner }

  const handlePageEnd = () => {
    if (!isEndReached) {
      setCurrentPage(prevCurrentPage => prevCurrentPage + 1)
    }
  }

  const renderEmptyState = () => {
    return (
      <ShowAtBreakpoint breakpoint="portables">
        <ConversationListEmptyState />
      </ShowAtBreakpoint>
    )
  }

  const renderAd = () => {
    return (
      <Suspense key="ad">
        <div
          key="slot-inbetween"
          className="conversation-list__ad"
          data-testid="slot-container"
          suppressHydrationWarning
        >
          <Advertisement shape={AdShape.Inbetween} id="slot-inbetween" />
        </div>
      </Suspense>
    )
  }

  const renderConversation = (conversation: ConversationListItemType) => {
    return <ConversationListItem key={conversation.id} conversation={conversation} />
  }

  const renderItem = (item: ConversationListItemType | 'ad') => {
    switch (item) {
      case 'ad':
        return renderAd()
      default:
        return renderConversation(item)
    }
  }

  const renderItems = () => {
    if (!conversations.length) return renderEmptyState()

    const items: Array<ConversationListItemType | 'ad'> = [...conversations]

    if (breakpoints.portables) {
      items.splice(3, 0, 'ad')
    }

    return (
      <SeparatedList separator={<Divider testId="conversation-list-divider" />}>
        {items.map(renderItem)}
      </SeparatedList>
    )
  }

  const renderToolbar = () => (
    <div className="conversation-list-toolbar">
      <Navigation
        left={
          <Cell styling={Cell.Styling.Narrow}>
            <div className="u-ui-padding-left-small">
              <Text as="h2" type={Text.Type.Title} text={translate('title')} />
            </div>
          </Cell>
        }
      />
    </div>
  )

  const renderInfiniteScroll = () => {
    return (
      <>
        <Divider />
        <div className="u-fill-height u-overflow-auto" ref={infiniteScrollWrapRef}>
          <InfiniteScroll renderItems={renderItems} onPageEnd={handlePageEnd} />
        </div>
      </>
    )
  }

  const renderContent = () => {
    if (uiState === UiState.Pending) return <ContentLoader testId="loader" />

    return (
      <>
        {renderToolbar()}
        {renderInfiniteScroll()}
      </>
    )
  }

  const renderInfoBanner = () => {
    return <InfoBanner screen={Screen.MessageList} linkTracking={linkTracking} />
  }

  useEffect(() => {
    if (!breakpoints.portables) return
    if (!isInboxPage) return

    clearConversation()
  }, [clearConversation, breakpoints, isInboxPage])

  return (
    <>
      {renderInfoBanner()}
      {renderContent()}
    </>
  )
}

export default ConversationList
